import type React from 'react';
import { Bell, CalendarClock, LogOut, Upload, User, UserPlus } from 'lucide-react';
import { buildNav, configuredHideNav, settingsSections, type NavItem } from '@/components/layout/navConfig';
import { reportLinks } from '@/components/layout/Sidebar';

/** A palette command: go somewhere, or do something. Built from what the user may open (the sidebar's own nav,
 *  so module access, roles and HIDE_NAV apply), plus a few actions. */
export interface Command {
  id: string;
  label: string;
  /** Extra words that match (e.g. "bank" for Import statement). */
  keywords?: string;
  icon: React.ComponentType<{ className?: string }>;
  href?: string;
  external?: boolean;
  run?: 'logout';
}

export function buildCommands(ctx: { hasModule: (m: string) => boolean; roles: string[] }): Command[] {
  const isAdmin = ctx.roles.includes('admin');
  const nav = buildNav({ hasModule: ctx.hasModule, isAdmin, roles: ctx.roles, reportLinks: reportLinks(), hideNav: configuredHideNav(ctx.roles ?? []) });
  const items: NavItem[] = nav.flatMap(e => (e.kind === 'link' ? [e.item] : e.group.sections.flatMap(s => s.items)));
  // C67: the admin pages left the menu for the Settings hub; ⌘K still goes straight to each (once: Chase templates is
  // in both)
  for (const i of settingsSections({ hasModule: ctx.hasModule, isAdmin, roles: ctx.roles, hideNav: configuredHideNav(ctx.roles ?? []) })
    .flatMap(s => s.items)) {
    if (!items.some(x => x.href === i.href)) items.push(i);
  }
  const go: Command[] = items.map(i => ({
    id: `go:${i.href}`, label: `Go to ${i.label}`, icon: i.icon, href: i.href, external: i.external,
  }));
  const actions: Command[] = [];
  if (ctx.hasModule('bankrec')) {
    actions.push({ id: 'do:bank-upload', label: 'Import bank statements', keywords: 'upload bank statement csv', icon: Upload, href: '/bankrec/upload' });
  }
  if (isAdmin) {
    actions.push({ id: 'do:add-user', label: 'Add a user', keywords: 'new user invite', icon: UserPlus, href: '/admin/users' });
  }
  actions.push(
    { id: 'do:delegate', label: 'Delegate my work', keywords: 'delegation holiday cover absence', icon: CalendarClock, href: '/delegations' },
    { id: 'do:profile', label: 'My profile', keywords: 'account password', icon: User, href: '/profile' },
    { id: 'do:notifications', label: 'Notification settings', keywords: 'email alerts', icon: Bell, href: '/settings/notifications' },
    { id: 'do:logout', label: 'Sign out', keywords: 'log out logout', icon: LogOut, run: 'logout' },
  );
  return [...go, ...actions];
}

/** Commands whose label or keywords contain every word typed (any order, any case). */
export function matchCommands(commands: Command[], q: string): Command[] {
  const words = q.toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return commands;
  return commands.filter(c => {
    const text = `${c.label} ${c.keywords ?? ''}`.toLowerCase();
    return words.every(w => text.includes(w));
  });
}
